<template>
  <div>
    <ul
      class="list-box"
      ref="unsortedList"
      :class="{ 'list-items-has-search-btn': hasSearchButton }"
      tabindex="-1"
      @keydown.prevent.stop="handleKeydown"
      @keydown.enter="handleEnter"
      @keydown.tab.prevent="tabPressed"
      :style="{maxHeight: `${visibleItems * itemHeight}px`}"
    >
      <template v-if="options.length === 0">
        <span
            class="not-found-text"
        >
          Not found
        </span>
      </template>
      <template v-else-if="optionsHasObjects">
        <li
            v-for="option in options"
            :key="option.id"
            class="list-box-item"
            :class="{ 'is-selected': value && value.id === option.id || option.id === specialIndex,
        'is-focused': focusedOption && focusedOption.id === option.id,
        'disabled': option.id === specialIndex}"
            @mouseover="mouseover"
            @click="handleClick(option)"
        >
          <slot name="optionContent" :option="option">
            {{ option[optionsItemName] }}
          </slot>
        </li>
      </template>
      <template v-else>
        <li
            v-for="option in options"
            :key="option"
            class="list-box-item"
            :class="{ 'is-selected': value === option, 'is-focused': focusedOption === option }"
            @mouseover="mouseover"
            @click="handleClick(option)"
        >
          <slot name="optionContent" :option="option">
            {{ option }}
          </slot>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'BSelectBox',
  props: {
    value: {
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    optionsItemName: {
      type: String,
      default: 'name',
    },
    visibleItems: {
      type: Number,
      default: 5,
    },
    hasSearchButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focusedIndex: null,
      focusedOption: null,
      itemHeight: 30,
      specialIndex: -1, // if search didn't get a result/too much result
    };
  },
  computed: {
    optionsHasObjects() {
      if (this.options.length > 0) {
        return this.options[0].id !== undefined;
      }
      return false;
    },
  },
  methods: {
    tabPressed(event) {
      this.$emit('keydown', event);
    },
    mouseover() {
      const focusedElement = document.getElementsByClassName('is-focused')[0];
      if (focusedElement) {
        focusedElement.classList.remove('is-focused');
      }
    },
    focus() {
      const ulElement = document.querySelector('.list-box');
      if (ulElement) {
        ulElement.focus();
      }
    },
    handleClick(option) {
      if (option && option.id === this.specialIndex) return;
      this.$emit('click', option);
    },
    handleEnter() {
      if (this.focusedOption && this.focusedOption.id === this.specialIndex) return;
      this.$emit('enter', this.focusedOption);
    },
    handleKeydown(event) {
      if (!this.$el.contains(event.target)) return;
      switch (event.code) {
        case 'ArrowUp':
          this.navigate(-1);
          break;
        case 'ArrowDown':
          if (!this.focusedOption) {
            this.navigate(0);
          } else {
            this.navigate(1);
          }
          break;
        default:
          break;
      }
      this.$emit('keydown', event);
    },
    navigateTo(selectedItem) {
      this.focusedIndex = null; // reset focused index
      this.focusedOption = null;
      this.$nextTick(() => {
        if (selectedItem.id !== undefined) {
          this.focusedOption = this.options.find((option) => option.id === selectedItem.id);
        } else {
          this.focusedOption = this.options.find((option) => option === selectedItem);
        }
        this.navigate(0);
        this.$refs.unsortedList.focus();
      });
    },
    navigate(dir) {
      if (this.focusedOption) {
        let idx = null;
        if (this.focusedOption.id !== undefined) {
          idx = this.options.findIndex((option) => option.id === this.focusedOption.id);
        } else {
          idx = this.options.findIndex((option) => option === this.focusedOption);
        }
        if (this.options[idx + dir]) {
          this.focusedOption = this.options[idx + dir];
        }
      }
      this.$nextTick(() => {
        const selectedElement = this.$el.querySelector('.list-box-item.is-focused');
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
      });
    },
    scrollTo() {
      const focusedElement = this.$el.querySelector('.list-box-item.is-selected');
      if (focusedElement) {
        focusedElement.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    },
    // Just scroll to top. Without selecting or focused
    scrollToTop() {
      const focusedElement = this.$el.querySelector('.list-box');
      if (focusedElement) focusedElement.scrollTop = 0;
    },
    scrollToFocused() {
      const focusedElement = this.$el.querySelector('.list-box-item.is-focused');
      if (focusedElement) {
        focusedElement.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    },
    resetFocus() {
      const focused = this.$el.querySelectorAll('.list-box-item.is-focused');
      focused.forEach((elem) => {
        elem.classList.remove('is-focused');
      });
      this.focusedIndex = null;
    },
  },
};
</script>

<style scoped>
.list-box {
  border: 1px solid #ccc;
  border-radius: unset;
  overflow-y: auto;
  background-color: white;
}
.list-box.list-items-has-search-btn {
  border-top: 1px solid #ccc;
  border-left: unset;
  border-right: unset;
  border-bottom: unset;
}

.list-box:focus {
  outline: none;
}

.not-found-text {
  display: block;
  padding: 5px 9.5px 5px;
  font-weight: 400;
  font-size: 19px;
  color: #363636;
}

.list-box-item {
  padding: 0 9.5px 0;
  font-weight: 400;
  font-size: 19px;
  color: #363636;
  &:not(.disabled)
    cursor pointer
}
.list-box-item:hover {
  background-color: #007bff;
  color: white;
}

.list-box-item.is-focused {
  background-color: #007bff;
  color: white;
}

.list-box-item.is-selected:not(.disabled) {
  background-color: #ddd;
  color: #4a4a4a;
}
.list-box-item.is-selected.disabled {
  background-color: #8f8f8f;
  color: #4a4a4a;
  font-weight: 500;
}

.list-box-item.is-selected.is-focused {
  background-color: #007bff;
  color: white;
}

</style>
